import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import { DashboardLayout } from './components/layout/DashboardLayout'
import LoginPage from './pages/LoginPage'
import OnboardingPage from './pages/OnboardingPage'
import DashboardPage from './pages/DashboardPage'
import FloorPlanPage from './pages/FloorPlanPage'
import BookingsPage from './pages/BookingsPage'
import BookingDetailPage from './pages/BookingDetailPage'
import OpeningHoursPage from './pages/OpeningHoursPage'
import CustomersPage from './pages/CustomersPage'
import OrdersPage from './pages/OrdersPage'
import SettingsPage from './pages/SettingsPage'
import SheAnswersPage from './pages/SheAnswersPage'
import CustomerBookingPage from './pages/CustomerBookingPage'
import POSPage from './pages/POSPage'
import KitchenPage from './pages/KitchenPage'
import CollectionsPage from './pages/CollectionsPage'
import CouponsPage from './pages/CouponsPage'
import PlatformAdminPage from './pages/PlatformAdminPage'
import OrdevoNetworkPage from './pages/OrdevoNetworkPage'
import { DebugPanel } from './components/DebugPanel'
import i18next, { applyRTL } from './lib/i18n'
import { useAppStore } from './lib/store/useAppStore'
import { useThemeStore } from './lib/store/useThemeStore'

function App() {
  const languageCode = useAppStore((s) => s.languageCode)
  const themeMode = useThemeStore((s) => s.mode)
  const syncResolvedTheme = useThemeStore((s) => s.syncResolvedTheme)

  useEffect(() => {
    i18next.changeLanguage(languageCode)
    applyRTL(languageCode)
  }, [languageCode])

  useEffect(() => {
    syncResolvedTheme()
    if (themeMode !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => syncResolvedTheme()
    media.addEventListener?.('change', handler)
    return () => media.removeEventListener?.('change', handler)
  }, [themeMode, syncResolvedTheme])

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute requireOnboarding={false}>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/floor-plan" element={<FloorPlanPage />} />
            <Route path="/bookings" element={<BookingsPage />} />
            <Route path="/bookings/:id" element={<BookingDetailPage />} />
            <Route path="/opening-hours" element={<OpeningHoursPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/she-answers" element={<SheAnswersPage />} />
            <Route path="/customer-page" element={<CustomerBookingPage />} />
            {/* Legacy alias — old links / bookmarks still resolve. */}
            <Route path="/booking-page" element={<CustomerBookingPage />} />
            <Route path="/pos" element={<POSPage />} />
            <Route path="/coupons" element={<CouponsPage />} />
            <Route path="/collections" element={<CollectionsPage />} />
            <Route path="/ordevo-network" element={<OrdevoNetworkPage />} />
            <Route path="/platform" element={<PlatformAdminPage />} />
          </Route>
          {/* Kitchen display — full screen, no sidebar */}
          <Route
            path="/kitchen"
            element={
              <ProtectedRoute>
                <KitchenPage />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <DebugPanel />
    </AuthProvider>
  )
}

export default App
